import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CreditCard, User, MessageSquare, ArrowRight, Sparkles } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { useSubscription } from '@/hooks/useSubscription';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

// Mirrors Billing.tsx — the raw Stripe status maps to a translated label.
const STATUS_KEYS: Record<string, string> = {
  active: 'billing.statusActive',
  trialing: 'billing.statusTrialing',
  past_due: 'billing.statusPastDue',
  canceled: 'billing.statusCanceled',
  cancelled: 'billing.statusCanceled',
};

const STATUS_VARIANTS: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
  active: 'green',
  trialing: 'yellow',
  past_due: 'red',
  canceled: 'gray',
  cancelled: 'gray',
};

/**
 * Account home for SaaS tenants (e.g. whatarewemakingfordinner). The ecommerce
 * Dashboard's order stats / "Recent Orders" / "Start Shopping" make no sense
 * here, so this surface is purely account management: subscription status,
 * billing, profile, and support.
 *
 * Note: this intentionally has NO "open the app / launch" call to action — the
 * customer experiences the product as the site itself and reaches it through
 * normal navigation (and the silent `/` → AuthorizedHomeRedirect hop for
 * subscribers). They don't know there's an embedded app behind the scenes.
 */
export default function SaasDashboard() {
  const { t } = useTranslation('account');
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useSubscription();

  const subscription = data?.subscription ?? null;
  const isSubscribed = !!subscription || !!user?.hasActiveSubscription;

  const fmtDate = (iso: string | null | undefined): string => {
    if (!iso) return t('billing.dateUnavailable');
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const quickLinks: { label: string; description: string; href: string; icon: any }[] = [
    ...(isSubscribed
      ? [{
          label: t('dashboard.saas.quickLinks.billing.label'),
          description: t('dashboard.saas.quickLinks.billing.description'),
          href: '/account/billing',
          icon: CreditCard,
        }]
      : []),
    {
      label: t('dashboard.quickLinks.profile.label'),
      description: t('dashboard.quickLinks.profile.description'),
      href: '/account/profile',
      icon: User,
    },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome */}
      <section aria-label={t('dashboard.welcomeAriaLabel')}>
        <h2 className="text-xl font-display font-bold text-ink">
          {user?.firstName
            ? t('dashboard.welcomeWithName', { name: user.firstName })
            : t('dashboard.welcomeFallback')}
        </h2>
        <p className="text-ink-muted mt-1">{t('dashboard.saas.subtitle')}</p>
      </section>

      {/* Subscription */}
      <section aria-label={t('dashboard.saas.subscriptionAriaLabel')}>
        <h3 className="text-lg font-semibold text-ink mb-4">
          {t('dashboard.saas.subscriptionHeading')}
        </h3>

        {isLoading ? (
          <LoadingSpinner className="py-12" />
        ) : subscription ? (
          <Card>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-xl font-bold text-ink">
                    {subscription.plan_name || t('dashboard.saas.planFallback')}
                  </h4>
                  <Badge variant={STATUS_VARIANTS[subscription.status] || 'gray'}>
                    {STATUS_KEYS[subscription.status]
                      ? t(STATUS_KEYS[subscription.status])
                      : subscription.status}
                  </Badge>
                </div>
                <p className="text-sm text-ink-muted mt-2">
                  {subscription.cancel_at_period_end
                    ? t('dashboard.saas.cancelsOn', { date: fmtDate(subscription.current_period_end) })
                    : t('dashboard.saas.renewsOn', { date: fmtDate(subscription.current_period_end) })}
                </p>
              </div>
              {subscription.plan_price != null && subscription.plan_interval && (
                <div className="text-right text-ink-soft shrink-0">
                  <span className="text-xl font-bold text-ink">
                    {t('billing.pricePerInterval', {
                      amount: Number(subscription.plan_price).toFixed(0),
                    })}
                  </span>
                  <span className="ml-1 text-sm">
                    {t('billing.perInterval', { interval: subscription.plan_interval })}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-4 border-t border-line pt-4">
              <Link
                to="/account/billing"
                className="text-sm font-medium text-sage-700 hover:text-sage-800 inline-flex items-center gap-1"
              >
                {t('dashboard.saas.manageBilling')}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Card>
        ) : (
          <Card>
            <div className="text-center py-8">
              <Sparkles className="h-12 w-12 text-ink-muted mx-auto mb-3" aria-hidden="true" />
              <p className="font-medium text-ink">{t('dashboard.saas.noPlanTitle')}</p>
              <p className="text-ink-muted mt-1 mb-4">{t('dashboard.saas.noPlanBody')}</p>
              <Link to="/pricing">
                <Button>{t('dashboard.saas.viewPlans')}</Button>
              </Link>
            </div>
          </Card>
        )}
      </section>

      {/* Manage account */}
      <section aria-label={t('dashboard.quickLinksAriaLabel')}>
        <h3 className="text-lg font-semibold text-ink mb-4">
          {t('dashboard.saas.manageHeading')}
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {quickLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Link
                key={link.href}
                to={link.href}
                className="flex items-start gap-3 bg-surface rounded-lg border border-line p-4 hover:border-sage-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-muted shrink-0">
                  <Icon className="h-5 w-5 text-ink-soft" aria-hidden="true" />
                </div>
                <div>
                  <p className="font-medium text-ink">{link.label}</p>
                  <p className="text-sm text-ink-muted">{link.description}</p>
                </div>
              </Link>
            );
          })}

          {/* Support opens the Axiss chat widget, matching AccountLayout. */}
          <button
            type="button"
            data-axiss-open="chat"
            className="flex items-start gap-3 bg-surface rounded-lg border border-line p-4 text-left hover:border-sage-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 focus-visible:ring-offset-2"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-surface-muted shrink-0">
              <MessageSquare className="h-5 w-5 text-ink-soft" aria-hidden="true" />
            </div>
            <div>
              <p className="font-medium text-ink">{t('dashboard.saas.quickLinks.support.label')}</p>
              <p className="text-sm text-ink-muted">{t('dashboard.saas.quickLinks.support.description')}</p>
            </div>
          </button>
        </div>
      </section>
    </div>
  );
}
